/**
 * Requests (client and internal), escalations and client tasks — ADR-001 §7.2, §11.5, guide §5.9, §6.3.
 * "Nothing is lost": every ask becomes a tracked item with an owner and a clock.
 *
 * Copy (types, purposes, statuses, needed-by choices) is the prototype's exact wording
 * (docs/prototype.html REQTYPES, PURPOSES, RSTAT, the needed-by and "Running late" selects).
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, Version, derived, page } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
import { CloseTaskBody, Closure, DropTaskBody, OriginRef, Task } from './tasks.js';
const BoolString = z.enum(['true', 'false']).transform((s) => s === 'true');
const Text = (min = 3, max = 2000) => z.string().trim().min(min).max(max);
// ============================================================================ client requests
/** How the client asked. The prototype's "Came in by" list (includes Meeting). */
export const RequestVia = z.enum(['Email', 'Call', 'WhatsApp', 'Meeting']).meta({ id: 'RequestVia' });
/**
 * `new` is legacy: nothing triages client requests, so they are created `open` (rows still `new` were moved to
 * `open` by db/policies/0015). It stays in the enum only so an old row can never fail a read; treat it as `open`.
 */
export const ClientRequestStatus = z.enum(['new', 'open', 'needs_details', 'done']).meta({ id: 'ClientRequestStatus' });
export const CLIENT_REQUEST_STATUS_LABEL = {
    new: 'New',
    open: 'Open',
    needs_details: 'Needs details',
    done: 'Done',
};
/** The response clock (guide §6.3): `hours_left` goes negative once breached; explained like any derived value. */
export const RequestClock = derived(z.object({ hours_left: z.number(), is_breached: z.boolean() })).meta({ id: 'RequestClock' });
/** A thread entry — internal requests and (Ops-only) client requests alike. */
export const RequestMessage = z
    .object({
    id: Uuid,
    /** `null` for system entries (status changes, delays). */
    by: UserRef.nullable(),
    body: z.string(),
    at: Timestamp,
    /** Ops-only notes; never sent to a requester. Always `false` on a client request, whose whole thread is Ops. */
    is_internal_note: z.boolean(),
    kind: z.enum(['message', 'system']),
})
    .meta({ id: 'RequestMessage' });
export const ClientRequest = z
    .object({
    id: Uuid,
    client: ClientRef,
    via: RequestVia,
    text: z.string(),
    owner: UserRef.nullable(),
    received_at: Timestamp,
    respond_by_at: Timestamp,
    status: ClientRequestStatus,
    clock: RequestClock,
    closed_at: Timestamp.nullable(),
    /** Proof of closure (same shape and checking rules as tasks). */
    closure: Closure.nullable(),
    escalation_id: Uuid.nullable(),
    /** Tasks created from this request. */
    task_ids: z.array(Uuid),
    origin_ref: OriginRef.nullable(),
    /** The Conversation, oldest first — on `GET /client-requests/{id}` and after a post; absent from lists. */
    messages: z.array(RequestMessage).optional(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
})
    .meta({ id: 'ClientRequest' });
export const ClientRequestPage = page(ClientRequest).meta({ id: 'ClientRequestPage' });
export const ClientRequestIdParams = z.object({ id: Uuid });
export const ListClientRequestsQuery = PageQuery.extend({
    /** Default: everything not done. */
    status: ClientRequestStatus.optional(),
    client_id: Uuid.optional(),
    owner_id: Uuid.optional(),
    /** `true` = past the response time only. */
    breached: BoolString.optional(),
});
/**
 * Log a client request; it is created `open`. The clock starts now: `respond_by_at = received_at +
 * settings.request.client_response_hours` (24). The owner defaults to the client's current SPOC; only the associate
 * director may pick someone else (403).
 */
export const CreateClientRequestBody = z
    .object({
    client_id: Uuid,
    via: RequestVia,
    text: Text(3, 2000),
    owner_id: Uuid.optional(),
    /** When it actually came in, if earlier than now (not in the future). */
    received_at: Timestamp.optional(),
})
    .strict();
/** `done` is only reachable through POST …/close (proof). Changing `owner_id` needs the associate director. */
export const UpdateClientRequestBody = z
    .object({
    status: ClientRequestStatus.exclude(['done', 'new']).optional(),
    owner_id: Uuid.optional(),
    text: Text(3, 2000).optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
export const EscalateBody = z
    .object({
    severity: z.enum(['high', 'med', 'low']),
    /** Default: the request text. */
    title: Text(3, 200).optional(),
    note: Text(1, 2000).optional(),
})
    .strict();
/** Default due: today when 8 hours or less are left on the clock, else tomorrow; priority follows the clock. */
export const RequestToTaskBody = z.object({ assignee_id: Uuid.optional(), due: IsoDate.optional() }).strict();
// ============================================================================ internal requests
/** Prototype REQTYPES: [label, help]. */
export const InternalRequestType = z
    .enum(['usage_summary', 'data_pull', 'deck_support', 'onboarding', 'campaign_setup', 'something_else'])
    .meta({ id: 'InternalRequestType' });
export const INTERNAL_REQUEST_TYPE_LABEL = {
    usage_summary: { label: 'Usage summary', help: 'Numbers on how a client uses our products' },
    data_pull: { label: 'Report or data pull', help: 'MIS, extracts or a custom cut of data' },
    deck_support: { label: 'Deck or proposal support', help: 'Slides, case studies, pricing inputs' },
    onboarding: { label: 'New client onboarding', help: 'Handing a closed deal over to Ops' },
    campaign_setup: { label: 'Campaign or journey setup', help: 'A WhatsApp, SMS or email set-up' },
    something_else: { label: 'Something else', help: 'Anything that does not fit above' },
};
/** Prototype PURPOSES — "What is it for?" */
export const RequestPurpose = z
    .enum(['client_meeting', 'proposal', 'client_promise', 'internal_use', 'other'])
    .meta({ id: 'RequestPurpose' });
export const REQUEST_PURPOSE_LABEL = {
    client_meeting: 'A client meeting',
    proposal: 'A proposal or pitch',
    client_promise: 'Something promised to a client',
    internal_use: 'Internal use',
    other: 'Other',
};
/** The prototype's "Needed by" and "New expected date" choices, in days from today. */
export const NEED_DAYS = [0, 1, 2, 3, 4, 7, 10, 14];
export const NeedDays = z.literal(NEED_DAYS).meta({ id: 'NeedDays' });
export const InternalRequestStatus = z
    .enum(['waiting_triage', 'in_progress', 'needs_details', 'waiting_confirm', 'done', 'declined'])
    .meta({ id: 'InternalRequestStatus' });
/** Prototype RSTAT labels (what the requester reads). */
export const INTERNAL_REQUEST_STATUS_LABEL = {
    waiting_triage: 'Waiting for triage',
    in_progress: 'In progress',
    needs_details: 'Needs more details',
    waiting_confirm: 'Waiting for your confirmation',
    done: 'Done',
    declined: 'Declined',
};
/**
 * The acknowledgement clock (guide §6.3): 24 hours to acknowledge; paused while `needs_details` (until the
 * requester replies); after breach the request is flagged to the associate director.
 */
export const AckClock = derived(z.object({
    hours_to_ack: z.number().nullable(),
    is_breached: z.boolean(),
    paused: z.boolean(),
    /**
     * Why it is paused: waiting for the requester's reply, or a company holiday in the published calendar
     * (operator decision: the internal clock pauses on company holidays; client request clocks never do).
     */
    paused_reason: z.enum(['needs_details', 'holiday']).nullable(),
})).meta({ id: 'AckClock' });
/** A client as a requester may see it: a name, nothing more (requesters cannot read clients). */
export const ClientName = z.object({ id: Uuid, name: z.string() }).meta({ id: 'ClientName' });
/** A client on the request form: its name and current SPOC, who "Who in Ops should do it?" preselects. */
export const ClientOption = ClientName.extend({ spoc: UserRef.nullable() }).meta({ id: 'ClientOption' });
/**
 * A file attached to an internal request (the detail's "Files" chips). Open it with
 * GET /internal-requests/{id}/files/{n}, `n` = its position in `files` (0-based).
 */
export const RequestFile = z.object({ key: z.string(), name: z.string() }).meta({ id: 'RequestFile' });
/** Largest file a request may carry. */
export const REQUEST_FILE_MAX_BYTES = 25 * 1024 * 1024;
/** POST /internal-requests/sign-upload — one slot per file; the browser PUTs the file, then sends the key in `file_keys`. */
export const SignRequestUploadBody = z
    .object({
    filename: z.string().trim().min(1).max(200),
    bytes: z.number().int().positive().max(REQUEST_FILE_MAX_BYTES, 'The file is over 25 MB. Compress it, split it or paste a link.'),
    content_type: z.string().trim().toLowerCase().max(120),
})
    .strict();
/** GET /internal-requests/{id}/files/{n} */
export const RequestFileParams = z.object({ id: Uuid, n: z.coerce.number().int().min(0).max(4) });
export const InternalRequest = z
    .object({
    id: Uuid,
    team: z.enum(['sales', 'marketing']),
    by: UserRef,
    /** Set when a manager logged it on the requester's behalf. */
    logged_by: UserRef.nullable(),
    type: InternalRequestType,
    client: ClientName.nullable(),
    prospect: z.string().nullable(),
    text: z.string(),
    purpose: RequestPurpose,
    needed_by: IsoDate,
    why_it_matters: z.string(),
    wanted_spoc: UserRef.nullable(),
    owner: UserRef.nullable(),
    status: InternalRequestStatus,
    raised_at: Timestamp,
    ack_by_at: Timestamp.nullable(),
    acked_at: Timestamp.nullable(),
    /** Who acknowledged it ("Acknowledged: {name}, {time}"); set and cleared together with `acked_at`. */
    acked_by: UserRef.nullable(),
    clock: AckClock,
    /** Ops' expected finish and, when late, why (shown to the requester). */
    expected_by: IsoDate.nullable(),
    expected_why: z.string().nullable(),
    reopened_count: z.number().int().nonnegative(),
    task_id: Uuid.nullable(),
    closure: Closure.nullable(),
    files: z.array(RequestFile),
    /** Present on single-request reads; a requester never receives internal notes. */
    messages: z.array(RequestMessage).optional(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
})
    .meta({ id: 'InternalRequest' });
export const InternalRequestPage = page(InternalRequest).meta({ id: 'InternalRequestPage' });
export const InternalRequestIdParams = z.object({ id: Uuid });
export const ListInternalRequestsQuery = PageQuery.extend({
    status: InternalRequestStatus.optional(),
    team: z.enum(['sales', 'marketing']).optional(),
    client_id: Uuid.optional(),
});
/** The request form's choices. Requesters cannot read clients, so the client list comes from here. */
export const InternalRequestOptions = z
    .object({
    /** Active clients by name, each with its current SPOC (the form preselects the SPOC as the doer). */
    clients: z.array(ClientOption),
    /** People who can do the work ("Who in Ops should do it?"). */
    ops_people: z.array(UserRef),
    types: z.array(z.object({ key: InternalRequestType, label: z.string(), help: z.string() })),
    purposes: z.array(z.object({ key: RequestPurpose, label: z.string() })),
    need_days: z.array(z.object({ days: NeedDays, label: z.string() })),
})
    .meta({ id: 'InternalRequestOptions' });
/**
 * Raise a request (sales, marketing). A manager may log one on a requester's behalf with `on_behalf_of_id`.
 * `ack_by_at = now + 24 h` (company holidays do not count).
 * `file_keys` must be keys of finished uploads made BY THE CALLER through POST /internal-requests/sign-upload (a manager
 * logging on someone's behalf uploads them). Any other key, or one whose upload has not arrived, is 422 on `file_keys`
 * — no insecure direct object reference. Up to 5, each once.
 */
export const CreateInternalRequestBody = z
    .object({
    type: InternalRequestType,
    client_id: Uuid.optional(),
    prospect: Text(2, 120).optional(),
    text: Text(10, 2000),
    purpose: RequestPurpose,
    needed_by_days: NeedDays,
    why_it_matters: Text(3, 500),
    wanted_spoc_id: Uuid.optional(),
    file_keys: z.array(z.string().min(1).max(500)).max(5, 'Attach at most 5 files').optional(),
    on_behalf_of_id: Uuid.optional(),
})
    .strict()
    .refine((b) => !(b.client_id && b.prospect), { path: ['prospect'], message: 'Pick a client or name a prospect, not both' });
/**
 * Triage (managers). `accept` needs `owner_id` and creates a task titled "[{Team}] {text}" due in
 * `expected_days`; `ask_details` needs `note` and pauses the clock; `decline` needs `note` and writes a
 * dropped item. Only from `waiting_triage` (409 otherwise).
 */
export const TriageBody = z
    .object({
    action: z.enum(['accept', 'ask_details', 'decline']),
    owner_id: Uuid.optional(),
    expected_days: NeedDays.optional(),
    note: Text(3, 1000).optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.action === 'accept' && !b.owner_id)
        ctx.addIssue({ code: 'custom', path: ['owner_id'], message: 'Pick who in Ops will do it' });
    // ADR §11.5: accept "sets owner + expected date (creates a task)" — the task's due date is this.
    if (b.action === 'accept' && b.expected_days === undefined) {
        ctx.addIssue({ code: 'custom', path: ['expected_days'], message: 'Say when Ops expects to finish' });
    }
    if (b.action !== 'accept' && !b.note) {
        ctx.addIssue({ code: 'custom', path: ['note'], message: b.action === 'decline' ? 'Say why Ops cannot take it' : 'Ask your question' });
    }
});
export const TriageResponse = z.object({ request: InternalRequest, task: Task.nullable() }).meta({ id: 'TriageResponse' });
/** A requester reply while `needs_details` resumes the clock (new `ack_by_at`). Requesters cannot post internal notes. */
export const PostMessageBody = z
    .object({ body: Text(1, 2000), is_internal_note: z.boolean().optional() })
    .strict();
export const PostMessageResponse = z
    .object({ message: RequestMessage, request: InternalRequest })
    .meta({ id: 'PostMessageResponse' });
/**
 * "Add a message" on a client request. The thread is Ops-only (the client is not a user), so there is no internal
 * note flag and posting never moves the response clock.
 */
export const PostClientRequestMessageBody = z.object({ body: Text(1, 2000) }).strict();
export const PostClientRequestMessageResponse = z
    .object({ message: RequestMessage, request: ClientRequest })
    .meta({ id: 'PostClientRequestMessageResponse' });
/** "Running late?" — a new expected date and why; the requester sees it straight away. */
export const DelayBody = z.object({ expected_days: NeedDays, why: Text(3, 500) }).strict();
/** Close with proof (as tasks) and a note to the requester → `waiting_confirm`. */
export const CloseInternalRequestBody = CloseTaskBody.safeExtend({ note: Text(3, 1000) });
/**
 * Only the requester who raised it. `done` finishes it; `not_yet` (needs `what_is_missing`) sends it back
 * to `in_progress` with a fresh 24-hour clock, `reopened_count + 1` and a "[Reopened] …" task for the owner.
 */
export const ConfirmBody = z
    .object({ outcome: z.enum(['done', 'not_yet']), what_is_missing: Text(3, 1000).optional() })
    .strict()
    .superRefine((b, ctx) => {
    if (b.outcome === 'not_yet' && !b.what_is_missing) {
        ctx.addIssue({ code: 'custom', path: ['what_is_missing'], message: 'Say what is still missing' });
    }
});
export const ConfirmResponse = z.object({ request: InternalRequest, task: Task.nullable() }).meta({ id: 'ConfirmResponse' });
// ---------------------------------------------------------------- notifications (in-app only in v1)
export const Notification = z
    .object({
    id: Uuid,
    text: z.string(),
    at: Timestamp,
    request_id: Uuid.nullable(),
    read: z.boolean(),
})
    .meta({ id: 'Notification' });
export const NotificationPage = page(Notification).meta({ id: 'NotificationPage' });
export const ListNotificationsQuery = PageQuery.extend({ unread: BoolString.optional() });
export const MarkReadBody = z.object({ ids: z.array(Uuid).min(1).max(200) }).strict();
/**
 * The "Typical turnaround" tile: median calendar days from raised to closed (status `done`; the close time is
 * Ops' latest closure) over the caller's team's requests closed in the last 90 days, to one decimal.
 */
export const TypicalTurnaround = z
    .object({
    median_days: z.number().nonnegative(),
    /** How many closed requests the median is over (at least 5). */
    sample: z.number().int().min(5),
})
    .meta({ id: 'TypicalTurnaround' });
/** The My requests tiles (prototype stats row). */
export const MyRequestsStats = z
    .object({
    /** Waiting for triage, in progress or needing details (prototype isOpenReq). */
    open: z.number().int().nonnegative(),
    /** Needing details or waiting for the requester's confirmation — what the menu badge counts too. */
    waiting_on_you: z.number().int().nonnegative(),
    /** Done (confirmed) requests whose latest Ops closure falls in the current calendar quarter, IST. */
    done_this_quarter: z.number().int().nonnegative(),
})
    .meta({ id: 'MyRequestsStats' });
/** GET /me/requests — the requester's page: own requests (including waiting_confirm) and notifications. */
export const MyRequestsResponse = z
    .object({
    data: z.array(InternalRequest),
    notifications: z.array(Notification),
    unread_count: z.number().int().nonnegative(),
    /** `null` while fewer than 5 of the team's requests closed in the last 90 days (too few to be typical). */
    typical_turnaround: TypicalTurnaround.nullable(),
    /** The page's tiles, over ALL the caller's own requests (not only the ones in `data`). */
    stats: MyRequestsStats,
})
    .meta({ id: 'MyRequestsResponse' });
// ============================================================================ escalations
export const Severity = z.enum(['high', 'med', 'low']).meta({ id: 'Severity' });
export const EscalationNote = z
    .object({ id: Uuid, by: UserRef.nullable(), at: Timestamp, body: z.string() })
    .meta({ id: 'EscalationNote' });
/** Escalations are never raised automatically — always by a person (ADR §8.4). Each open one costs 15 health points. */
export const Escalation = z
    .object({
    id: Uuid,
    client: ClientRef,
    severity: Severity,
    title: z.string(),
    owner: UserRef.nullable(),
    opened_on: IsoDate,
    status: z.enum(['open', 'closed']),
    closed_on: IsoDate.nullable(),
    /** The dated log, oldest first. */
    notes: z.array(EscalationNote),
    closure: Closure.nullable(),
    client_request_id: Uuid.nullable(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
})
    .meta({ id: 'Escalation' });
export const EscalationPage = page(Escalation).meta({ id: 'EscalationPage' });
export const EscalationIdParams = z.object({ id: Uuid });
export const ListEscalationsQuery = PageQuery.extend({
    status: z.enum(['open', 'closed']).optional(),
    client_id: Uuid.optional(),
    severity: Severity.optional(),
});
/** Owner defaults to the client's current SPOC; only the associate director may pick someone else. */
export const CreateEscalationBody = z
    .object({ client_id: Uuid, severity: Severity, title: Text(3, 200), owner_id: Uuid.optional(), note: Text(1, 2000).optional() })
    .strict();
export const UpdateEscalationBody = z
    .object({ severity: Severity.optional(), title: Text(3, 200).optional(), owner_id: Uuid.optional() })
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
export const EscalationNoteBody = z.object({ body: Text(1, 2000) }).strict();
// ============================================================================ client tasks (commitments)
/**
 * What Amplispot told a client it would do. **Never called a "promise" in the UI** (guide §9).
 * Creating one also creates a linked task (priority high) — the link is stored both ways.
 */
export const ClientTask = z
    .object({
    id: Uuid,
    client: ClientRef,
    what: z.string(),
    promised_by: UserRef,
    /** The client contact it was said to. */
    promised_to: z.string(),
    due: IsoDate,
    status: z.enum(['open', 'kept', 'dropped']),
    overdue: z.boolean(),
    days_overdue: z.number().int().nonnegative(),
    kept_at: Timestamp.nullable(),
    closure: Closure.nullable(),
    /** e.g. "Meeting 28 Sep", "Email 2 Oct". */
    source: z.string().nullable(),
    task_id: Uuid.nullable(),
    origin_ref: OriginRef.nullable(),
    version: Version,
    created_at: Timestamp,
})
    .meta({ id: 'ClientTask' });
export const ClientTaskPage = page(ClientTask).meta({ id: 'ClientTaskPage' });
export const ClientTaskIdParams = z.object({ id: Uuid });
export const ListClientTasksQuery = PageQuery.extend({
    status: z.enum(['open', 'kept', 'dropped']).optional(),
    client_id: Uuid.optional(),
    overdue: BoolString.optional(),
});
export const CreateClientTaskBody = z
    .object({
    client_id: Uuid,
    what: Text(3, 300),
    promised_to: Text(2, 120),
    due: IsoDate,
    source: Text(2, 120).optional(),
    /** Default: the caller. Who told the client. */
    promised_by_id: Uuid.optional(),
})
    .strict();
export const CreateClientTaskResponse = z
    .object({ client_task: ClientTask, task: Task })
    .meta({ id: 'CreateClientTaskResponse' });
/** Marking kept needs proof (as tasks); it also closes the linked task with the same proof. */
export const KeptBody = CloseTaskBody;
export const DropClientTaskBody = DropTaskBody;
// ---------------------------------------------------------------- proof check on request / escalation closures
export const ItemClosureParams = z.object({ item_kind: z.enum(['client_request', 'escalation']), item_id: Uuid });
export const ItemCheckResponse = z
    .object({ item_kind: z.enum(['client_request', 'escalation']), item_id: Uuid, closure: Closure })
    .meta({ id: 'ItemCheckResponse' });
// ---------------------------------------------------------------- who checks each closure (review t-3074965e)
/**
 * Every route that records a proof-of-closure has exactly one way for that proof to be checked
 * (operator decision 12: never by the closer):
 *  - tasks.close            → POST /closures/{task_id}/check                      (Review queue kind `proof`)
 *  - clientTasks.kept       → the proof closes the LINKED TASK, checked as above  (one proof, one check)
 *  - clientRequests.close   → POST /closures/client_request/{id}/check            (kind `request_proof`)
 *  - escalations.close      → POST /closures/escalation/{id}/check                (kind `escalation_proof`)
 *  - internalRequests.close → no AD check: the REQUESTER confirms it (internalRequests.confirm) — the
 *                             person who asked is the one who can say it is done (ADR §11.5)
 */
export const CLOSURE_CHECK_PATH = {
    'tasks.close': 'closures.check',
    'clientTasks.kept': 'closures.check',
    'clientRequests.close': 'closures.checkItem',
    'escalations.close': 'closures.checkItem',
    'internalRequests.close': 'internalRequests.confirm',
};
//# sourceMappingURL=requests.js.map